import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useViewport } from '@/hooks';
import { media } from '@/theme/media';
import { useSettingsSections } from '@/hooks';
import { sectionIcons } from './sectionIcons';
import { SectionNavMobile } from '@/app/shell/SectionNavMobile';
import { SettingsSidebar } from './SettingsSidebar';
import { SettingsHeader } from './SettingsHeader';
import { useSettingsSidebar } from './useSettingsSidebar';
import { AccountSection } from './sections/AccountSection';
import { AppearanceSection } from './sections/AppearanceSection';
import { PrivacySection } from './sections/PrivacySection';
import { NotificationsSection } from './sections/NotificationsSection';
import { AboutSection } from './sections/AboutSection';
import { AccessibilitySection } from './sections/AccessibilitySection';
import { DataStorageSection } from './sections/DataStorageSection';
import { LanguageRegionSection } from './sections/LanguageRegionSection';
import { ContentSection } from './sections/ContentSection';
import { SoundVideoSection } from './sections/SoundVideoSection';
import { ConnectedAccountsSection } from './sections/ConnectedAccountsSection';
import { ComingSoonSection } from './sections/ComingSoonSection';

const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.bgRoot};
`;

const Body = styled.div<{ $collapsed: boolean }>`
  display: grid;
  grid-template-columns: ${({ $collapsed }) => ($collapsed ? '76px' : '280px')} minmax(0, 1fr);
  /* Full-bleed canvas: fills the viewport width, and the flex grow stretches it
     down to the bottom edge instead of stopping at content height (the sidebars
     already declare height:100% for exactly this). Grow-only, never shrink, so
     content taller than the viewport still scrolls normally. */
  flex: 1 0 auto;
  transition: grid-template-columns var(--interaction-duration, 160ms) ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  @media (min-width: 768px) and (max-width: 1023.98px) {
    grid-template-columns: ${({ $collapsed }) => ($collapsed ? '76px' : '230px')} minmax(0, 1fr);
  }
  ${media.mobile`grid-template-columns: minmax(0, 1fr);`}
`;

const Pane = styled.main<{ $updates: boolean; $dashboard?: boolean }>`
  min-width: 0;
  padding: ${({ $updates, $dashboard }) =>
    $updates ? '28px 32px 48px' : $dashboard ? '32px 36px 48px' : '48px'};
  max-width: ${({ $updates, $dashboard }) =>
    $updates ? '1800px' : $dashboard ? '1440px' : '1280px'};
  width: 100%;
  ${media.tabletDown`padding: 32px 24px;`}
  ${media.mobile`padding: 24px 16px;`}
`;

const SectionHeading = styled.h2`
  margin: 0 0 32px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl};
  line-height: ${({ theme }) => theme.lineHeight.xl};
  font-weight: 600;
`;

/* 1.27.70 Account Settings intro (owner concept account_settings_v2): the heading,
   a one-line summary, and the platform's line on the right. */
const Intro = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 32px;
  margin-bottom: 24px;
  > div > h2 {
    margin: 0;
  }
`;

const IntroText = styled.p`
  margin: 4px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Motto = styled.figure`
  margin: 0;
  text-align: right;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textMuted};
  > blockquote {
    margin: 0;
    font-style: italic;
  }
  > figcaption {
    margin-top: 2px;
  }
  ${media.tabletDown`display: none;`}
`;

function AccountIntro({ label }: { label: string }) {
  return (
    <Intro>
      <div>
        <SectionHeading>{label}</SectionHeading>
        <IntroText>Manage your account, security, and preferences all in one place.</IntroText>
      </div>
      <Motto>
        <blockquote>A more open, authentic social experience.</blockquote>
        <figcaption>TruXSocial</figcaption>
      </Motto>
    </Intro>
  );
}

/**
 * Desktop sidebar — always in the DOM (sidebar link tests rely on this).
 * CSS hides it below the mobile breakpoint; SectionNavMobile takes its place.
 */
const SidebarWrap = styled.div<{ $collapsed: boolean }>`
  min-width: 0;
  position: sticky;
  top: 78px;
  height: calc(100dvh - 78px);
  align-self: start;
  overflow-y: auto;
  scrollbar-width: thin;
  padding: ${({ $collapsed }) => ($collapsed ? '24px 10px' : '32px 24px')};
  background: ${({ theme }) => theme.color.surfaceChrome};
  border-right: 1px solid ${({ theme }) => theme.color.glassBorder};
  @media (max-width: 1023.98px) {
    padding: ${({ $collapsed }) => ($collapsed ? '24px 10px' : '32px 16px')};
  }
  ${media.mobile`display: none;`}
`;

/**
 * Mobile section-switcher — always in the DOM.
 * CSS hides it on tablet/desktop; JS viewport check also gates it so
 * the Popover doesn't capture keyboard events on desktop.
 */
const MobileNavWrap = styled.div`
  margin-bottom: 16px;
  /* Visible only on mobile via media query; display toggled by JS below */
  ${media.tabletUp`display: none;`}
`;

function SectionContent({ activeId }: { activeId: string }) {
  switch (activeId) {
    case 'account':
      return <AccountSection />;
    case 'appearance':
      return <AppearanceSection />;
    case 'privacy':
      return <PrivacySection />;
    case 'notifications':
      return <NotificationsSection />;
    case 'about':
      return <AboutSection />;
    case 'accessibility':
      return <AccessibilitySection />;
    case 'data':
      return <DataStorageSection />;
    case 'language':
      return <LanguageRegionSection />;
    case 'content':
      return <ContentSection />;
    case 'sound':
      return <SoundVideoSection />;
    case 'connected':
      return <ConnectedAccountsSection />;
    default:
      return <ComingSoonSection sectionId={activeId} />;
  }
}

export interface SettingsLayoutProps {
  activeId: string;
}

export function SettingsLayout({ activeId }: SettingsLayoutProps) {
  const navigate = useNavigate();
  const viewport = useViewport();
  const collapsed = useSettingsSidebar((state) => state.collapsed);
  const { sections } = useSettingsSections();

  const mobileSections = sections.map((s) => ({
    id: s.id,
    label: s.label,
    icon: sectionIcons[s.id],
  }));

  function handleSectionSelect(id: string) {
    navigate(`/settings/${id}`, { replace: true });
  }

  const isMobile = viewport === 'mobile';

  return (
    <Page>
      <SettingsHeader />
      <Body $collapsed={collapsed}>
        {/* Sidebar: always rendered; CSS hides on mobile */}
        <SidebarWrap $collapsed={collapsed} data-testid="settings-sidebar-wrap">
          <SettingsSidebar activeId={activeId} collapsed={collapsed} />
        </SidebarWrap>
        <Pane
          $updates={activeId === 'about'}
          $dashboard={activeId === 'account'}
          id="settings-main"
          tabIndex={-1}
        >
          {/* Mobile nav: rendered only when viewport is mobile (CSS also hides on tablet/desktop) */}
          {isMobile && (
            <MobileNavWrap data-testid="settings-mobile-nav-wrap">
              <SectionNavMobile
                sections={mobileSections}
                activeId={activeId}
                onSelect={handleSectionSelect}
              />
            </MobileNavWrap>
          )}
          {activeId === 'account' ? (
            <AccountIntro label={sections.find((s) => s.id === 'account')?.label ?? 'Settings'} />
          ) : (
            activeId !== 'about' && (
              <SectionHeading>
                {sections.find((s) => s.id === activeId)?.label ?? 'Settings'}
              </SectionHeading>
            )
          )}
          <SectionContent activeId={activeId} />
        </Pane>
      </Body>
    </Page>
  );
}
